import { events } from "@constants/tracking";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppStore } from "@hooks/useAppStore";
import type {
	BaseItemSchema,
	ItemResponse,
	OriginalItemTypes,
} from "@memorang/types";
import { useToast } from "@memorang/ui/components/Toast";
import { trackCustomEvent } from "analytics";
import { useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import ItemRenderer from "../components/itemtypes/question/ItemRenderer";
import { SESSION_EVENTS } from "../constants";
import useAIFeedbackStore from "../hooks/useAIFeedbackStore";
import useSessionStore from "../hooks/useSessionStore";
import { handlePostAnswer } from "./StudySessionBottombarContainer/helpers";
import useAnswerEvent from "./StudySessionBottombarContainer/useAnswerEvent";

interface Props {
	item: BaseItemSchema;
	response: ItemResponse;
	originalItemType: OriginalItemTypes;
	toggleShowReadMore?: (options?: { scrollToHighlight?: boolean }) => void;
}

const ItemRendererContainer = ({
	item,
	response,
	originalItemType,
	toggleShowReadMore,
}: Props) => {
	const { showToast } = useToast();

	const {
		currentExamContext: { projectId, organizationId, examId },
	} = useExamContext();
	const { mutateTrackAnswerEvent } = useAnswerEvent();

	const updateItemStates = useSessionStore((state) => state.updateItemStates);
	const itemStates = useSessionStore((state) => state.itemStates);
	const isSummativeTest = useSessionStore((state) => state.isSummativeTest);
	const inReviewMode = useSessionStore((state) => state.inReviewMode);
	const setUserResponse = useAIFeedbackStore((state) => state.setUserResponse);
	const [startTime, setStartTime] = useState(new Date());
	const { index } = useLocalSearchParams<{
		index: string;
	}>();
	const sessionId = useSessionStore((state) => state.id);
	const addSessionEvent = useSessionStore((state) => state.addSessionEvent);

	const shouldShowCrossout = useAppStore(
		(state) => state.sessionView?.showCrossOut,
	);

	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const itemId = item.id!;

	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	useEffect(() => {
		if (isSummativeTest) {
			setStartTime(new Date());
		}
	}, [itemId, isSummativeTest]);
	const userResponse = useAIFeedbackStore((state) => state.userResponse);
	const currentUserResponse = userResponse?.[itemId];

	const itemState = itemStates?.get(itemId);
	const isAnswered = isSummativeTest
		? inReviewMode && item.answer != null
		: itemStates?.get(itemId)?.answered || item.answer != null;

	const choices = response.type === "choice" ? response.choices : [];
	const selectedChoiceIds =
		itemState?.selectedChoiceIds || item.answer?.selectedChoiceIds;
	const correctAnswerIds =
		itemState?.correctAnswerIds ||
		choices?.filter((choice) => choice.isCorrect).map((choice) => choice.id);

	const crossedOutChoiceIds = itemState?.crossedOutChoiceIds;

	const correctAnswerOrders =
		response.type === "choice"
			? response.correctResponse?.map((choice) => choice.id)
			: undefined;

	const isMultiSelect =
		response.type === "choice" && response.selectionType === "multiple";

	const shouldPreventSelection = (choiceId: string) => {
		return (
			isMultiSelect &&
			response.maxCorrectChoices &&
			selectedChoiceIds &&
			selectedChoiceIds.length >= response.maxCorrectChoices &&
			!selectedChoiceIds.includes(choiceId)
		);
	};

	const handleAddSessionEvent = ({
		choiceId,
		crossingOut,
	}: {
		choiceId: string;
		crossingOut?: boolean;
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	}) => {
		if (crossingOut) {
			const isAlreadyCrossedOut = crossedOutChoiceIds?.includes(choiceId);
			if (!isAlreadyCrossedOut) {
				addSessionEvent(itemId, {
					name: SESSION_EVENTS.CHOICE_CROSSED_OUT,
					props: { choiceId },
				});
			}
		} else {
			const isCurrentlySelected = selectedChoiceIds?.includes(choiceId);

			if (isMultiSelect) {
				if (isCurrentlySelected) {
					addSessionEvent(itemId, {
						name: SESSION_EVENTS.CHOICES_UNSELECTED,
						props: {
							choiceIds: [choiceId],
						},
					});
				} else {
					const newSelections = [...(selectedChoiceIds || []), choiceId];
					addSessionEvent(itemId, {
						name: SESSION_EVENTS.CHOICES_SELECTED,
						props: {
							choiceIds: newSelections,
						},
					});
				}
			} else if (isCurrentlySelected) {
				addSessionEvent(itemId, {
					name: SESSION_EVENTS.CHOICES_UNSELECTED,
					props: {
						choiceIds: [choiceId],
					},
				});
			} else {
				if (selectedChoiceIds && selectedChoiceIds.length > 0) {
					addSessionEvent(itemId, {
						name: SESSION_EVENTS.CHOICES_UNSELECTED,
						props: {
							choiceIds: selectedChoiceIds,
						},
					});
				}

				addSessionEvent(itemId, {
					name: SESSION_EVENTS.CHOICES_SELECTED,
					props: {
						choiceIds: [choiceId],
					},
				});
			}
		}
	};

	const handleSelectChoice = (
		choiceId: string,
		crossingOut?: boolean,
		currentOrders?: string[],
	) => {
		if (shouldPreventSelection(choiceId) && response.type === "choice") {
			showToast({
				message: `Maximum limit reached. You can only select up to ${response.maxCorrectChoices} answers.`,
				duration: 500,
			});
			return;
		}

		trackCustomEvent({
			eventName: crossingOut
				? events.sessionItemCrossedOut
				: events.sessionItemOptionSelected,
			choiceId,
			itemId: itemId,
			sessionId: sessionId,
			isSummativeTest: isSummativeTest ? "true" : "false",
		});

		handleAddSessionEvent({
			choiceId,
			crossingOut,
		});

		updateItemStates({
			itemId,
			selectedChoiceId: choiceId,
			multiSelect: isMultiSelect,
			crossOut: crossingOut,
			currentOrders,
		});

		if (isSummativeTest) {
			handlePostAnswer({
				state: useSessionStore.getState(),
				startTime,
				projectId,
				organizationId,
				examId,
				submitAnswer: true,
				currentItemIndex,
				mutateTrackAnswerEvent,
				action: "select",
			});
		}
	};

	const handleHighlightedTextPress = () => {
		toggleShowReadMore?.({ scrollToHighlight: true });
	};

	const handleUpdateFormStates = (formStates: Record<string, string>) => {
		setUserResponse(itemId, formStates);
	};

	return (
		<ItemRenderer
			item={item}
			originalItemType={originalItemType}
			selectedChoiceIds={selectedChoiceIds}
			correctAnswerIds={correctAnswerIds}
			crossedOutChoiceIds={crossedOutChoiceIds}
			isAnswered={isAnswered}
			inReviewMode={inReviewMode}
			currentUserResponse={currentUserResponse}
			isSummativeTest={isSummativeTest}
			shouldShowCrossout={shouldShowCrossout}
			onSelectChoice={handleSelectChoice}
			toggleShowReadMore={toggleShowReadMore}
			correctAnswerOrders={correctAnswerOrders}
			handleHighlightedTextPress={handleHighlightedTextPress}
			updateFormStates={handleUpdateFormStates}
		/>
	);
};

export default ItemRendererContainer;
